import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { length } from 'ramda'
import { useQuery } from '@apollo/react-hooks'

import Seasons from './seasons'
import EmblaCarousel from '../emblaCarousel'
import CarouselTile from '../carousel/tile'
import { TrailerThumbnail } from '../../containers/shared/trailers/trailer'
import { COMPONENT_NAME_SERIES_DETAIL } from '../../lib/analytics/custom-dimensions'
import { getFirstTrailer, getResponsiveTileShowCount } from '../../lib/utils'
import { isSmallerThan480 } from '../../lib/screen'
import PLAYBACK_INFO_QUERY from '../../../graphql/queries/playback-info.gql'
import { useScreenWidth } from '../../hooks/use-screen-width'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import styles from './series-list.css'

const TABS = {
  episodes: 'episodes',
  trailers: 'trailers',
  recommendedTiles: 'recommendedTiles'
}
const TABS_LABELS = {
  episodes: 'EPISODES',
  trailers: 'TRAILERS',
  recommendedTiles: 'YOU MAY ALSO LIKE'
}

const TABS_LABELS_SAMLL_SCREEN = {
  episodes: 'EPISODES',
  trailers: 'TRAILERS',
  recommendedTiles: 'RELATED'
}

function SeriesList({
  series,
  isContentItemOnMyList,
  addToMyList,
  removeFromMyList,
  theme,
  margin
}) {
  const [selectedTab, setSelectedTab] = useState(TABS.episodes)
  const isAuthenticated = useIsAuthenticated()
  const screenWidth = useScreenWidth()
  if (!series || !length(series.seasons)) {
    return null
  }

  const {
    data: playbackInfoData,
    loading: playbackInfoLoading,
    error: playbackInfoError
  } = useQuery(PLAYBACK_INFO_QUERY, {
    variables: {
      contentItemId: series.id
    },
    fetchPolicy: 'network-only',
    skip: !isAuthenticated
  })

  const recommendedTiles = series.recommendedTiles || []
  const isShowYouMayAlsoLikeTab = recommendedTiles.length > 0
  const isShowTrailer = getFirstTrailer(series)
  if (!isShowYouMayAlsoLikeTab && TABS.recommendedTiles === selectedTab) setSelectedTab(TABS.episodes)

  const isSamllScreen = isSmallerThan480({ screenWidth })
  const videoExtras = series.videoExtras || []
  const trailersShowCount = getResponsiveTileShowCount(screenWidth, 1)
  const tilesShowCount = getResponsiveTileShowCount(screenWidth, 2)

  return (
    <div className={styles.seriesListWrapper}>
      <div
        style={{
          marginLeft: margin,
          marginRight: margin,
          width: `calc(100% - ${margin * 2}px)`
        }}
      >
        <span className={styles.breakline} />

        <div
          className={styles.seriesListTabWrapper}
        >
          <div
            className={classNames(styles.tab, {
              [styles.active]: TABS.episodes === selectedTab
            })}
            onClick={() => { setSelectedTab(TABS.episodes) }}
          >
            {isSamllScreen ? TABS_LABELS_SAMLL_SCREEN[TABS.episodes] : TABS_LABELS[TABS.episodes]}
          </div>

          {
            isShowTrailer && (
              <div
                className={classNames(styles.tab, {
                  [styles.active]: TABS.trailers === selectedTab
                })}
                onClick={() => { setSelectedTab(TABS.trailers) }}
              >
                {isSamllScreen ? TABS_LABELS_SAMLL_SCREEN[TABS.trailers] : TABS_LABELS[TABS.trailers]}
              </div>
            )
          }

          {
            isShowYouMayAlsoLikeTab && (
              <div
                className={classNames(styles.tab, {
                  [styles.active]: TABS.recommendedTiles === selectedTab
                })}
                onClick={() => { setSelectedTab(TABS.recommendedTiles) }}
              >
                {isSamllScreen ? TABS_LABELS_SAMLL_SCREEN[TABS.recommendedTiles] : TABS_LABELS[TABS.recommendedTiles]}
              </div>
            )
          }
        </div>

        {
          TABS.episodes === selectedTab && !playbackInfoLoading && !playbackInfoError && (
            <div className={styles.seasonsFrame}>
              <Seasons
                series={series}
                playbackInfoData={playbackInfoData}
                seasons={series.seasons}
                screenWidth={screenWidth}
              />
            </div>
          )
        }
      </div>

      {
        TABS.trailers === selectedTab && videoExtras.length > 0 && (
          <div className={styles.container}>
            <EmblaCarousel
              slidesToShow={trailersShowCount}
              activeSlideCount={videoExtras.length}
            >
              {videoExtras.map((trailer, index) => {
                return (
                  <div
                    className={classNames(styles.emblaSlide, styles.isTrailerSlide)}
                    key={trailer.id}
                  >
                    <TrailerThumbnail
                      trailer={trailer}
                      id={trailer.id}
                      contentItem={series}
                      contentItemId={series && series.id}
                      renderedInComponent={COMPONENT_NAME_SERIES_DETAIL}
                    />
                  </div>
                )
              })}
            </EmblaCarousel>
          </div>
        )
      }

      {
        TABS.recommendedTiles === selectedTab && (
          <div className={classNames(styles.container, styles.recommendedTilesContainer)}>
            <EmblaCarousel
              slidesToShow={tilesShowCount}
              activeSlideCount={recommendedTiles.length}
            >
              {recommendedTiles.map((tile, index) => {
                return (
                  <div
                    className={styles.emblaSlide}
                    key={tile.id}
                  >
                    <CarouselTile
                      tile={tile}
                      theme={theme}
                      index={index}
                      contentType="Series"
                      listTitle="You May Also Like"
                      addToMyList={addToMyList}
                      removeFromMyList={removeFromMyList}
                      isOnMyList={isContentItemOnMyList(tile.contentItem.id)}
                    />
                  </div>
                )
              })}
            </EmblaCarousel>
          </div>
        )
      }
    </div>
  )
}

SeriesList.propTypes = {
  series: PropTypes.oneOfType([PropTypes.object]).isRequired,
  isContentItemOnMyList: PropTypes.func.isRequired,
  addToMyList: PropTypes.func.isRequired,
  removeFromMyList: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  margin: PropTypes.number.isRequired
}

SeriesList.defaultProps = {
  selectedTab: TABS.episodes,
  screenWidth: window.innerWidth
}

export default SeriesList
